import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import classnames from 'classnames'
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Button from '../../../../components/Form/Button'
import ConfirmDialogShell from '../../tenant/components/billing/ConfirmDialogShell'
import CreateTenantActionWarnings from './CreateTenantActionWarnings'
import type {
  CreateTenantPreview,
  OnboardingConflict,
  OnboardingConflictType,
} from '../../../../apis/registration'
import {
  distinctPropertyBaseNames,
  isCommonAreaTenantName,
  propertyNamesAlign,
  registrationDisplayPropertyName,
} from '../constants'

export interface ConflictPreviewServiceAgreement {
  serviceAgreementId: string
  meterNumber: string
}

export interface ConflictPreviewUtilityAccount {
  name: string
  number: string
  agreements: ConflictPreviewServiceAgreement[]
}

export interface CreateTenantConflictRecordSummary {
  businessName: string
  property: string
  accounts: ConflictPreviewUtilityAccount[]
}

export interface CreateTenantConflictModalProps {
  isOpen: boolean
  preview: CreateTenantPreview | null
  recordSummary: CreateTenantConflictRecordSummary | null
  pmaWarningKind: 'rejected' | 'pending' | null
  recordHasFormatWarnings: boolean
  /** When true, PMA/TESA warnings are hidden (NGOM/VNEM and National accounts are agreement-exempt). */
  isAgreementExempt?: boolean
  isSaving: boolean
  onCancel: () => void
  onMerge: (targetTenantAccountCuid: string) => void
  onCreateAnyway: () => void
}

type ActionChoice = 'merge' | 'create'
type StructureNodeState = 'existing' | 'new' | 'merged'

interface StructureTreeLine {
  depth: number
  label: string
  state: StructureNodeState
}

interface MergeTargetOption {
  tenantCuid: string
  tenantAccountCuid: string
  tenantName: string
  tenantAccountNumber: string
  propertyName: string
  utilityAccountName: string
  utilityAccountNumber: string
  label: string
}

function mergeTargetsFromConflicts(
  conflicts: OnboardingConflict[],
  accounts: ConflictPreviewUtilityAccount[],
  fallbackPropertyName?: string,
): MergeTargetOption[] {
  const seen = new Set<string>()
  const targets: MergeTargetOption[] = []
  const registrationProperty = fallbackPropertyName?.trim() ?? ''

  for (const conflict of conflicts) {
    if (
      conflict.type !== 'UTILITY_ACCOUNT_ON_OTHER_TENANT' &&
      conflict.type !== 'CONTACT_EMAIL_AT_PROPERTY' &&
      conflict.type !== 'DUPLICATE_ACTIVE_FEED'
    ) {
      continue
    }

    const cuid = conflict.existingTenantAccountCuid
    if (cuid == null || seen.has(cuid)) continue

    // Defense-in-depth: never offer the landlord Common Area (CAM) account as a merge target. CAM
    // shares the property's contact info with its tenants, so a shared-email match against it is
    // noise (backend already filters these — guard here too).
    if (isCommonAreaTenantName(conflict.existingBusinessName)) {
      continue
    }

    // Defense-in-depth: never offer a cross-property merge target. Backend conflict detection is
    // already scoped to the same property, but if a target at a different property is ever surfaced
    // we drop it here so the operator can't accidentally merge across properties — the omnibus
    // failure mode behind WEB-6529 / WEB-6624.
    const existingProperty = conflict.existingPropertyName?.trim()
    if (
      registrationProperty !== '' &&
      existingProperty != null &&
      existingProperty !== '' &&
      !propertyNamesAlign(registrationProperty, existingProperty)
    ) {
      continue
    }
    seen.add(cuid)

    const uaNumber = conflict.utilityAccountNumber ?? accounts[0]?.number ?? ''
    const feedUa = accounts.find((ua) => ua.number === uaNumber) ?? accounts[0]
    const tenantName = conflict.existingBusinessName ?? 'Existing tenant'
    const tenantAccountNumber = conflict.existingTenantAccountNumber ?? '—'
    const utilityAccountName =
      conflict.existingUtilityAccountName ?? feedUa?.name ?? 'Utility account'
    const utilityAccountNumber = uaNumber || feedUa?.number || '—'

    targets.push({
      tenantCuid: conflict.existingTenantCuid ?? '',
      tenantAccountCuid: cuid,
      tenantName,
      tenantAccountNumber,
      propertyName: conflict.existingPropertyName?.trim() || fallbackPropertyName?.trim() || '—',
      utilityAccountName,
      utilityAccountNumber,
      label: `${tenantName} (${tenantAccountNumber})`,
    })
  }

  return targets.sort((a, b) => a.tenantName.localeCompare(b.tenantName))
}

function getPropertyBasedRecommendation(
  registrationPropertyName: string | undefined,
  mergeTargets: MergeTargetOption[],
): { recommendMerge: boolean; recommendCreate: boolean } {
  const registrationProperty = registrationPropertyName?.trim()
  if (registrationProperty == null || registrationProperty === '' || mergeTargets.length === 0) {
    return { recommendMerge: false, recommendCreate: false }
  }

  const targetProperties = [
    ...new Set(mergeTargets.map((target) => target.propertyName.trim()).filter((name) => name !== '—')),
  ]
  if (targetProperties.length === 0) {
    return { recommendMerge: false, recommendCreate: false }
  }

  const allTargetsOnRegistrationProperty = targetProperties.every((name) =>
    propertyNamesAlign(registrationProperty, name),
  )

  return {
    recommendMerge: allTargetsOnRegistrationProperty,
    recommendCreate: !allTargetsOnRegistrationProperty,
  }
}

function getDefaultActionChoice(
  canMerge: boolean,
  canCreate: boolean,
  registrationPropertyName: string | undefined,
  mergeTargets: MergeTargetOption[],
): ActionChoice | null {
  const { recommendMerge, recommendCreate } = getPropertyBasedRecommendation(registrationPropertyName, mergeTargets)
  if (recommendMerge && canMerge) return 'merge'
  if (recommendCreate && canCreate) return 'create'
  if (canMerge) return 'merge'
  if (canCreate) return 'create'
  return null
}

const ActionRecommendedBadge: React.FC = () => (
  <span className="ml-1.5 text-xs font-normal text-indigo-600 dark:text-indigo-400">(Recommended)</span>
)

interface ConflictSummaryItem {
  message: string
  href?: string
  linkAriaLabel?: string
}

function tenantConflictHref(conflict: OnboardingConflict): string | undefined {
  if (conflict.existingTenantCuid == null) return undefined
  const accountNumber = conflict.existingTenantAccountNumber ?? ''
  return `/tenant/tenants/${conflict.existingTenantCuid}?serviceAccountNumber=${encodeURIComponent(accountNumber)}`
}

function conflictSummaryItemFromConflict(conflict: OnboardingConflict): ConflictSummaryItem {
  if (conflict.type === 'DUPLICATE_ACTIVE_FEED' && conflict.existingFeedCuid != null) {
    return {
      message: conflict.message,
      href: `/tenant/onboarding/${conflict.existingFeedCuid}`,
      linkAriaLabel: 'Open onboarding record in new tab',
    }
  }

  const href = tenantConflictHref(conflict)
  if (href != null) {
    return {
      message: conflict.message,
      href,
      linkAriaLabel: `Open ${conflict.existingBusinessName ?? 'tenant'} in new tab`,
    }
  }

  return { message: conflict.message }
}

const CONFLICT_MODAL_TITLE: Record<OnboardingConflictType, string> = {
  DUPLICATE_ACTIVE_FEED: 'Existing onboarding conflict',
  CONTACT_EMAIL_AT_PROPERTY: 'Existing contact conflict',
  UTILITY_ACCOUNT_ON_OTHER_TENANT: 'Existing tenant or utility account conflict',
}

const CONFLICT_MODAL_TITLE_PRIORITY: OnboardingConflictType[] = [
  'DUPLICATE_ACTIVE_FEED',
  'CONTACT_EMAIL_AT_PROPERTY',
  'UTILITY_ACCOUNT_ON_OTHER_TENANT',
]

function getConflictModalTitle(conflicts: OnboardingConflict[]): string {
  const conflictTypes = [...new Set(conflicts.map((conflict) => conflict.type))]
  if (conflictTypes.length === 1) {
    return CONFLICT_MODAL_TITLE[conflictTypes[0]] ?? 'Existing conflict'
  }

  for (const type of CONFLICT_MODAL_TITLE_PRIORITY) {
    if (conflictTypes.includes(type)) {
      return CONFLICT_MODAL_TITLE[type]
    }
  }

  return 'Existing conflict'
}

function summarizeConflictMessages(
  conflicts: OnboardingConflict[],
  mergeableTargetCuids: Set<string>,
): ConflictSummaryItem[] {
  const uaConflicts = conflicts.filter((c) => c.type === 'UTILITY_ACCOUNT_ON_OTHER_TENANT')
  const otherConflicts = conflicts.filter((c) => c.type !== 'UTILITY_ACCOUNT_ON_OTHER_TENANT')
  const messages: ConflictSummaryItem[] = []

  const byUtilityAccount = new Map<string, OnboardingConflict[]>()
  for (const conflict of uaConflicts) {
    const key = conflict.utilityAccountNumber ?? 'unknown'
    const group = byUtilityAccount.get(key) ?? []
    group.push(conflict)
    byUtilityAccount.set(key, group)
  }

  for (const [utilityAccountNumber, group] of byUtilityAccount) {
    if (group.length === 1) {
      messages.push(conflictSummaryItemFromConflict(group[0]))
      continue
    }

    const propertyNames = distinctPropertyBaseNames(
      group.map((c) => c.existingPropertyName?.trim() ?? '').filter(Boolean),
    )

    let propertyClause = ''
    if (propertyNames.length === 1) {
      propertyClause = ` on ${propertyNames[0]}`
    } else if (propertyNames.length > 1) {
      propertyClause = ` across ${propertyNames.length} properties`
    }

    // Only prompt for a recipient when at least one of these accounts is a valid (same-property)
    // merge target. When the utility account is an omnibus account used only by tenants at other
    // properties, merge is not offered — explain that a new tenant will be created here instead.
    const hasMergeableTarget = group.some(
      (c) => c.existingTenantAccountCuid != null && mergeableTargetCuids.has(c.existingTenantAccountCuid),
    )
    const actionClause = hasMergeableTarget
      ? ' Choose which tenant account should receive the new service agreements.'
      : ' These are at other properties, so a new tenant will be created for this registration instead.'

    messages.push({
      message: `Utility account ${utilityAccountNumber} is associated with ${group.length} existing tenant accounts${propertyClause}.${actionClause}`,
    })
  }

  for (const conflict of otherConflicts) {
    messages.push(conflictSummaryItemFromConflict(conflict))
  }

  return messages
}

function agreementTreeLabel(agreement: ConflictPreviewServiceAgreement): string {
  return agreement.serviceAgreementId
    ? `SA: ${agreement.serviceAgreementId}`
    : `Meter: ${agreement.meterNumber || '—'}`
}

function normalizeUtilityAccountNumber(number?: string): string {
  return number?.trim() ?? ''
}

function buildMergeStructureTree(
  target: MergeTargetOption,
  accounts: ConflictPreviewUtilityAccount[],
  registrationBusinessName: string,
): StructureTreeLine[] {
  const hasProperty = target.propertyName !== '—'
  const tenantDepth = hasProperty ? 1 : 0
  const taDepth = tenantDepth + 1
  const uaDepth = tenantDepth + 2
  const saDepth = tenantDepth + 3
  const lines: StructureTreeLine[] = []

  if (hasProperty) {
    lines.push({ depth: 0, label: `Property: ${target.propertyName}`, state: 'existing' })
  }

  lines.push({ depth: tenantDepth, label: `Tenant: ${target.tenantName}`, state: 'existing' })

  const targetUaNumber = normalizeUtilityAccountNumber(target.utilityAccountNumber)
  const matchingAccounts = accounts.filter(
    (ua) => normalizeUtilityAccountNumber(ua.number) === targetUaNumber,
  )
  const newAccounts = accounts.filter((ua) => normalizeUtilityAccountNumber(ua.number) !== targetUaNumber)

  if (accounts.length > 0) {
    lines.push(
      {
        depth: taDepth,
        label: `TA: ${target.tenantName} (${target.tenantAccountNumber})`,
        state: 'existing',
      },
      {
        depth: uaDepth,
        label: `UA: ${target.utilityAccountName} (${target.utilityAccountNumber})`,
        state: 'existing',
      },
      { depth: saDepth, label: '… existing service agreements', state: 'existing' },
    )

    for (const ua of matchingAccounts) {
      for (const agreement of ua.agreements) {
        lines.push({
          depth: saDepth,
          label: agreementTreeLabel(agreement),
          state: 'merged',
        })
      }
    }
  }

  for (const ua of newAccounts) {
    lines.push({
      depth: taDepth,
      label: `TA: ${registrationBusinessName}`,
      state: 'merged',
    })
    lines.push({
      depth: uaDepth,
      label: `UA: ${ua.name || 'Utility account'} (${ua.number || '—'})`,
      state: 'merged',
    })
    for (const agreement of ua.agreements) {
      lines.push({
        depth: saDepth,
        label: agreementTreeLabel(agreement),
        state: 'merged',
      })
    }
  }

  return lines
}

function buildCreateStructureTree(
  businessName: string,
  accounts: ConflictPreviewUtilityAccount[],
  propertyName?: string,
  propertyExists = false,
): StructureTreeLine[] {
  const trimmedProperty = propertyName?.trim()
  const hasProperty = trimmedProperty != null && trimmedProperty !== '' && trimmedProperty !== '—'
  const tenantDepth = hasProperty ? 1 : 0
  const lines: StructureTreeLine[] = []

  if (hasProperty) {
    lines.push({
      depth: 0,
      label: `Property: ${trimmedProperty}`,
      state: propertyExists === true ? 'existing' : 'new',
    })
  }

  lines.push({ depth: tenantDepth, label: `Tenant: ${businessName}`, state: 'new' })

  accounts.forEach((ua) => {
    lines.push({
      depth: tenantDepth + 1,
      label: `TA: ${businessName}`,
      state: 'new',
    })
    lines.push({
      depth: tenantDepth + 2,
      label: `UA: ${ua.name || 'Utility account'} (${ua.number || '—'})`,
      state: 'new',
    })
    for (const agreement of ua.agreements) {
      lines.push({ depth: tenantDepth + 3, label: agreementTreeLabel(agreement), state: 'new' })
    }
  })

  return lines
}

function stateBadgeLabel(state: StructureNodeState): string {
  if (state === 'existing') return 'Existing'
  if (state === 'merged') return 'Merged'
  return 'New'
}

function stateBadgeClasses(state: StructureNodeState): string {
  if (state === 'existing') {
    return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
  }
  if (state === 'merged') {
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100'
  }
  return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100'
}

function lineHighlightClasses(state: StructureNodeState): string {
  if (state === 'existing') return ''
  if (state === 'merged') return 'bg-amber-50/80 dark:bg-amber-950/30'
  return 'bg-emerald-50/80 dark:bg-emerald-950/30'
}

const TREE_PREFIX_CH = 3

const StructureTreePreview: React.FC<{ lines: StructureTreeLine[] }> = ({ lines }) => (
  <div className="rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/60 p-3 font-mono text-xs leading-relaxed overflow-x-auto">
    {lines.map((line, index) => (
      <div
        key={`${line.depth}-${line.label}-${index}`}
        className={classnames('flex items-start py-0.5 pr-2 rounded', lineHighlightClasses(line.state))}
      >
        {line.depth > 0 ? (
          <>
            <span
              aria-hidden
              className="shrink-0"
              style={{ width: `${(line.depth - 1) * TREE_PREFIX_CH}ch` }}
            />
            <span className="text-slate-400 dark:text-slate-500 shrink-0 w-[3ch]">|- </span>
          </>
        ) : null}
        <span className="flex-1 min-w-0 text-gray-800 dark:text-gray-100 break-words">{line.label}</span>
        {!line.label.startsWith('…') ? (
          <span
            className={classnames(
              'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-sans font-medium uppercase tracking-wide',
              stateBadgeClasses(line.state),
            )}
          >
            {stateBadgeLabel(line.state)}
          </span>
        ) : null}
      </div>
    ))}
  </div>
)

interface MergeTargetPickerProps {
  targets: MergeTargetOption[]
  selectedTarget: string
  disabled: boolean
  onSelectTarget: (tenantAccountCuid: string) => void
}

const MergeTargetPicker: React.FC<MergeTargetPickerProps> = ({
  targets,
  selectedTarget,
  disabled,
  onSelectTarget,
}) => (
  <fieldset className="space-y-2 font-sans" disabled={disabled}>
    <legend className="text-xs font-medium text-gray-600 dark:text-gray-300">
      Which tenant account should receive the service agreements?
    </legend>
    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
      {targets.map((target) => {
        const isSelected = selectedTarget === target.tenantAccountCuid
        return (
          <div
            key={target.tenantAccountCuid}
            role="radio"
            aria-checked={isSelected}
            tabIndex={disabled ? -1 : 0}
            className={classnames(
              'rounded-md border px-3 py-2 cursor-pointer transition-colors',
              isSelected
                ? 'border-indigo-400 bg-indigo-50/60 dark:bg-indigo-950/30 ring-1 ring-indigo-400/40'
                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500',
            )}
            onClick={() => onSelectTarget(target.tenantAccountCuid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelectTarget(target.tenantAccountCuid)
              }
            }}
          >
            <div className="flex items-start gap-2">
              <input
                type="radio"
                name="create-tenant-merge-target"
                className="mt-0.5"
                checked={isSelected}
                readOnly
                tabIndex={-1}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{target.tenantName}</p>
                {target.propertyName !== '—' ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Property: {target.propertyName}
                  </p>
                ) : null}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  King account {target.tenantAccountNumber} · UA {target.utilityAccountName} ({target.utilityAccountNumber})
                </p>
              </div>
              {target.tenantCuid ? (
                <Link
                  to={`/tenant/tenants/${target.tenantCuid}?serviceAccountNumber=${encodeURIComponent(target.tenantAccountNumber)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open tenant in new tab"
                  aria-label={`Open ${target.tenantName} in new tab`}
                  className="shrink-0 self-center rounded p-1 text-slate-400 hover:text-indigo-600 dark:text-slate-500 dark:hover:text-indigo-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </Link>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  </fieldset>
)

const CreateTenantConflictModal: React.FC<CreateTenantConflictModalProps> = ({
  isOpen,
  preview,
  recordSummary,
  pmaWarningKind,
  recordHasFormatWarnings,
  isAgreementExempt = false,
  isSaving,
  onCancel,
  onMerge,
  onCreateAnyway,
}) => {
  const registrationPropertyName = registrationDisplayPropertyName(
    preview?.propertyName,
    recordSummary?.property,
  )

  const mergeTargets = useMemo(
    () =>
      preview != null && recordSummary != null
        ? mergeTargetsFromConflicts(
          preview.conflicts,
          recordSummary.accounts,
          registrationPropertyName,
        )
        : [],
    [preview, recordSummary, registrationPropertyName],
  )
  const conflictSummary = useMemo(
    () =>
      preview != null
        ? summarizeConflictMessages(preview.conflicts, new Set(mergeTargets.map((t) => t.tenantAccountCuid)))
        : [],
    [preview, mergeTargets],
  )
  const conflictModalTitle = useMemo(
    () => (preview != null ? getConflictModalTitle(preview.conflicts) : 'Existing conflict'),
    [preview],
  )
  const [selectedAction, setSelectedAction] = useState<ActionChoice | null>(null)
  const [selectedTarget, setSelectedTarget] = useState<string>('')

  const canMerge = preview?.canMerge === true && mergeTargets.length > 0
  const canCreate = preview?.canCreate === true

  React.useEffect(() => {
    if (!isOpen) return

    const canMergeNow = preview?.canMerge === true && mergeTargets.length > 0
    const canCreateNow = preview?.canCreate === true
    setSelectedAction(getDefaultActionChoice(canMergeNow, canCreateNow, registrationPropertyName, mergeTargets))
    setSelectedTarget(mergeTargets[0]?.tenantAccountCuid ?? '')
  }, [isOpen, preview?.feedCuid, preview?.canMerge, preview?.canCreate, registrationPropertyName, mergeTargets])

  const selectedMergeTarget = mergeTargets.find((target) => target.tenantAccountCuid === selectedTarget)

  const mergeTreeLines =
    recordSummary != null && selectedMergeTarget != null
      ? buildMergeStructureTree(
          selectedMergeTarget,
          recordSummary.accounts,
          recordSummary.businessName,
        )
      : []

  const mergeTargetPropertyNames = useMemo(
    () =>
      distinctPropertyBaseNames(
        mergeTargets.map((target) => target.propertyName).filter((name) => name !== '—'),
      ),
    [mergeTargets],
  )
  const { recommendMerge, recommendCreate } = useMemo(
    () => getPropertyBasedRecommendation(registrationPropertyName, mergeTargets),
    [registrationPropertyName, mergeTargets],
  )

  const createPropertyExists = preview?.propertyExists === true

  const createTreeLines =
    recordSummary != null
      ? buildCreateStructureTree(
        recordSummary.businessName,
        recordSummary.accounts,
        registrationPropertyName,
        createPropertyExists,
      )
      : []

  if (preview == null || recordSummary == null) {
    return null
  }

  const continueDisabled =
    isSaving ||
    selectedAction == null ||
    (selectedAction === 'merge' && (!canMerge || selectedTarget === '')) ||
    (selectedAction === 'create' && !canCreate)

  const handleContinue = (): void => {
    if (selectedAction === 'merge') {
      if (!canMerge || selectedTarget === '') return
      onMerge(selectedTarget)
      return
    }
    if (selectedAction === 'create') {
      if (!canCreate) return
      onCreateAnyway()
    }
  }

  const mergeCardSelected = selectedAction === 'merge'
  const createCardSelected = selectedAction === 'create'
  const multipleMergeTargets = mergeTargets.length > 1

  const selectMergeTarget = (tenantAccountCuid: string): void => {
    setSelectedAction('merge')
    setSelectedTarget(tenantAccountCuid)
  }

  return (
    <ConfirmDialogShell isOpen={isOpen} maxWidthClassName="max-w-2xl">
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/40 shrink-0">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{conflictModalTitle}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            This onboarding record overlaps with existing data at {registrationPropertyName ?? 'this property'}.
            Review the conflicts and choose how to proceed.
          </p>
        </div>
      </div>

      <ul className="text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700 max-h-32 overflow-y-auto pl-0">
        {conflictSummary.map((item, idx) => (
          <li
            key={`conflict-summary-${idx}`}
            className="w-full px-4 py-2 text-gray-700 dark:text-gray-200 flex items-start gap-2"
          >
            <span className="flex-1 min-w-0">{item.message}</span>
            {item.href != null ? (
              <Link
                to={item.href}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                aria-label={item.linkAriaLabel ?? 'Open in new tab'}
                className="shrink-0 self-center rounded p-1 text-slate-400 hover:text-indigo-600 dark:text-slate-500 dark:hover:text-indigo-400"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </Link>
            ) : null}
          </li>
        ))}
      </ul>

      <CreateTenantActionWarnings
        pmaWarningKind={pmaWarningKind}
        recordHasFormatWarnings={recordHasFormatWarnings}
        isAgreementExempt={isAgreementExempt}
      />

      <fieldset className="space-y-3" disabled={isSaving}>
        <legend className="sr-only">Choose how to configure this onboarding record</legend>

        {canMerge ? (
          <div
            role="button"
            tabIndex={isSaving ? -1 : 0}
            className={classnames(
              'block rounded-lg border p-4 cursor-pointer transition-colors',
              mergeCardSelected
                ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-950/20'
                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500',
            )}
            onClick={() => setSelectedAction('merge')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setSelectedAction('merge')
              }
            }}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="create-tenant-conflict-action"
                className="mt-1"
                checked={mergeCardSelected}
                readOnly
                tabIndex={-1}
              />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    Merge with existing tenant
                    {recommendMerge ? <ActionRecommendedBadge /> : null}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Service agreements from this registration are added under the existing utility account.
                    {multipleMergeTargets
                      ? mergeTargetPropertyNames.length > 1
                        ? ` ${mergeTargets.length} existing tenant accounts share this utility account number across ${mergeTargetPropertyNames.length} properties — pick the correct recipient below.`
                        : mergeTargetPropertyNames.length === 1
                          ? ` ${mergeTargets.length} existing tenant accounts on ${mergeTargetPropertyNames[0]} share this utility account number — pick the correct recipient below.`
                          : ` ${mergeTargets.length} existing tenant accounts share this utility account number — pick the correct recipient below.`
                      : null}
                  </p>
                </div>

                {mergeCardSelected && multipleMergeTargets ? (
                  <MergeTargetPicker
                    targets={mergeTargets}
                    selectedTarget={selectedTarget}
                    disabled={isSaving}
                    onSelectTarget={selectMergeTarget}
                  />
                ) : null}

                {mergeCardSelected ? <StructureTreePreview lines={mergeTreeLines} /> : null}
              </div>
            </div>
          </div>
        ) : null}

        {canCreate ? (
          <div
            role="button"
            tabIndex={isSaving ? -1 : 0}
            className={classnames(
              'block rounded-lg border p-4 cursor-pointer transition-colors',
              createCardSelected
                ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-950/20'
                : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500',
            )}
            onClick={() => setSelectedAction('create')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setSelectedAction('create')
              }
            }}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="create-tenant-conflict-action"
                className="mt-1"
                checked={createCardSelected}
                readOnly
                tabIndex={-1}
              />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    Create new tenant
                    {recommendCreate ? <ActionRecommendedBadge /> : null}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    A new tenant, tenant account(s), and utility account row(s) are created from this registration. A
                    OneBill account-signup invitation is generated.
                  </p>
                </div>
                {createCardSelected ? <StructureTreePreview lines={createTreeLines} /> : null}
              </div>
            </div>
          </div>
        ) : null}
      </fieldset>

      <div className="flex flex-wrap gap-2 justify-end">
        <Button color="slate" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button isSaving={isSaving} disabled={continueDisabled} onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </ConfirmDialogShell>
  )
}

export default CreateTenantConflictModal
